import { ResolvedPlace } from "@/components/alerts/add-v2/model/types";

/**
 * Geocodes an address the user has just picked — **once**, at selection time. README decision 3.
 *
 * This is the single change that removes three problems the old flow has: `generateValidationCriteria`
 * geocodes every address on every keystroke (a billed request per character), `saveBulletin` then
 * geocodes the same addresses again, and it throws outright when `window.google.maps` has not loaded.
 *
 * Returns `null` rather than throwing when Maps is unavailable or the place cannot be resolved.
 * `BulletinLocation.resolved` is documented as null until an address resolves, and the type select
 * stays usable meanwhile — the design requires that, and a bulletin whose geocode failed is still
 * worth keeping as a draft.
 */
export const geocodePlace = async (
  placeId: string,
): Promise<ResolvedPlace | null> => {
  if (!window.google?.maps?.Geocoder) return null;

  try {
    const { results } = await new google.maps.Geocoder().geocode({ placeId });
    const result = results[0];
    if (!result) return null;

    return {
      placeId: result.place_id,
      formattedAddress: result.formatted_address,
      addressComponents: result.address_components,
      geometry: result.geometry.location.toJSON(),
      locationType: result.geometry.location_type,
      types: result.types,
      plusCode: result.plus_code,
    };
  } catch {
    return null;
  }
};
